import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { USER_ACCESS_TOKEN } from '@constants/auth';
import { CAMPAIGN_ID } from '@constants/common';
import { ROUTING } from '@constants/routing';
import CampaignDetails from '@pages/CampaignDetails';
import Dashboard from '@pages/Dashboard';
import LeadsDatabase from '@pages/LeadsDatabase';
import Pricing from '@pages/Pricing';
import Settings from '@pages/Settings';
import PrivatePreviewLayout from '@providers/layouts/PrivatePreviewLayout/PrivatePreviewLayout';
import PublicRegistrationGuard from '@providers/PublicRegistrationGuard';
import AdminRoute from '@providers/routes/AdminRoute';
import PrivateRoutes from '@providers/routes/PrivateRoutes';
import { RootState } from '@store/store';
import LeadScraper from 'src/pages/LeadScraper';

import { RouteErrorFallback } from '@components/ErrorBoundary';
import Loading from '@components/Loading/Loading';
import RegistrationLayout from '@components/RegistrationView';

import { ProtectedRoutesProvider } from './shared/ProtectedRoutesContext';

const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ConfirmEmail = lazy(() => import('./pages/ConfirmEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const CheckEmail = lazy(() => import('./pages/CheckEmail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const NotFound = lazy(() => import('./pages/NotFound'));
const CheckoutSuccess = lazy(() => import('./pages/Checkout/Success'));
const CheckoutCancelled = lazy(() => import('./pages/Checkout/Cancelled'));
const UnifiedMailbox = lazy(() => import('./pages/UnifiedMailbox'));
const AllCampaigns = lazy(() => import('./pages/AllCampaigns'));
// EmailCampaigns now redirects to AllCampaigns (merged view)
const EmailAccounts = lazy(() => import('./pages/EmailAccounts'));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'));
const EmailTemplateBuilder = lazy(() => import('./pages/EmailTemplateBuilder'));
// EmailAnalytics route now redirects to email-campaigns
const EmailWarmup = lazy(() => import('./pages/EmailWarmup'));
const VisualScraper = lazy(() => import('./components/VisualScraper'));
const QuickCampaign = lazy(() => import('./pages/QuickCampaign'));
const ImportCampaign = lazy(() => import('./pages/ImportCampaign'));
const DomainDashboard = lazy(() => import('./pages/CustomDomains').then(m => ({ default: m.DomainDashboard })));
const DomainPurchaseWizard = lazy(() =>
  import('./pages/CustomDomains').then(m => ({ default: m.DomainPurchaseWizard })),
);
const DomainDetailsPage = lazy(() => import('./pages/CustomDomains').then(m => ({ default: m.DomainDetailsPage })));
const EmailInfrastructureWizard = lazy(() =>
  import('./pages/CustomDomains').then(m => ({ default: m.EmailInfrastructureWizard })),
);
const ProvisioningProgress = lazy(() =>
  import('./pages/CustomDomains').then(m => ({ default: m.ProvisioningProgress })),
);
const HomeDashboard = lazy(() => import('./pages/HomeDashboard'));
// LeadPipeline route now redirects to database
const AdminWarmupPool = lazy(() => import('./pages/AdminWarmupPool'));
const AdminEngagementDashboard = lazy(() => import('./pages/AdminEngagementDashboard'));
const AdminDnsManagement = lazy(() => import('./pages/AdminDnsManagement'));
const OnboardingDashboard = lazy(() => import('./pages/OnboardingDashboard'));
const WarmupOnboarding = lazy(() => import('./pages/WarmupOnboarding'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

const PUBLIC_ROUTES = [
  { path: ROUTING.LOGIN, Component: Login },
  { path: ROUTING.SIGNUP, Component: Signup },
  { path: ROUTING.FORGOT_PASSWORD, Component: ForgotPassword },
  { path: ROUTING.CHECK_EMAIL, Component: CheckEmail },
  { path: ROUTING.RESET_PASSWORD, Component: ResetPassword },
];

/**
 * AuthGate: Renders at the root `/` path.
 * - Authenticated users → redirect to /dashboard (preserving any query
 *   string, so post-Stripe-Checkout `?checkout=success` survives the
 *   redirect and the dashboard can show a confirmation toast).
 * - Unauthenticated visitors who arrive with `?checkout=success` (e.g. their
 *   session lapsed during the Stripe round-trip) get bounced to /login
 *   carrying the same param so they land back on the dashboard with the
 *   confirmation visible.
 * - Everyone else → public landing page.
 */
const AuthGate = () => {
  const authState = useSelector((state: RootState) => state.auth);
  const hasToken = typeof window !== 'undefined' && localStorage.getItem(USER_ACCESS_TOKEN);
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const hasCheckoutReturn = /[?&]checkout=(success|cancelled)\b/.test(search);

  if (authState.isAuthenticated || hasToken) {
    return <Navigate to={`/${ROUTING.DASHBOARD}${search}`} replace />;
  }

  if (hasCheckoutReturn) {
    // Logged out but mid-checkout: send them through login so they land on
    // the dashboard with the toast still in the URL.
    return <Navigate to={`/${ROUTING.LOGIN}${search}`} replace />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <LandingPage />
    </Suspense>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteErrorFallback />,
    children: [
      // Public root: landing page or redirect to dashboard
      {
        index: true,
        element: <AuthGate />,
      },
      // Landing page also accessible at /home
      {
        path: ROUTING.LANDING,
        element: (
          <Suspense fallback={<Loading />}>
            <LandingPage />
          </Suspense>
        ),
      },
      // Legal pages (public, no auth required)
      {
        path: ROUTING.TERMS_OF_SERVICE,
        element: (
          <Suspense fallback={<Loading />}>
            <TermsOfService />
          </Suspense>
        ),
      },
      {
        path: ROUTING.PRIVACY_POLICY,
        element: (
          <Suspense fallback={<Loading />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      // Pricing is public so prospects can review plans before signing up.
      // The Pricing component branches on isAuthenticated to decide what
      // happens when a plan card is clicked (signup vs Stripe checkout).
      {
        path: ROUTING.PRICING,
        element: <Pricing />,
      },
      // Confirm email - accessible both authenticated and unauthenticated
      // (needed for email verification links and post-signup flow)
      {
        path: ROUTING.CONFIRM_EMAIL,
        element: (
          <Suspense fallback={<Loading />}>
            <RegistrationLayout>
              <ConfirmEmail />
            </RegistrationLayout>
          </Suspense>
        ),
      },
      {
        element: <PrivateRoutes />,
        children: [
          {
            errorElement: <RouteErrorFallback />,
            element: (
              <PrivatePreviewLayout>
                <ProtectedRoutesProvider>
                  <Dashboard />
                </ProtectedRoutesProvider>
              </PrivatePreviewLayout>
            ),
            children: [
              {
                path: ROUTING.DASHBOARD,
                element: (
                  <Suspense fallback={<Loading />}>
                    <HomeDashboard />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.LEAD_SCRAPERS,
                element: <Navigate to={`/${ROUTING.ALL_CAMPAIGNS}`} replace />,
              },
              {
                path: ROUTING.LEADS_DATABASE,
                element: <LeadsDatabase />,
              },
              {
                path: ROUTING.LEAD_PIPELINE,
                element: <Navigate to={`/${ROUTING.LEADS_DATABASE}`} replace />,
              },
              {
                path: ROUTING.SETTINGS,
                element: <Settings />,
              },
              {
                path: ROUTING.VISUAL_SCRAPER,
                element: (
                  <Suspense fallback={<Loading />}>
                    <VisualScraper />
                  </Suspense>
                ),
              },
              {
                path: `${ROUTING.CAMPAIGN_DETAILS}/:${CAMPAIGN_ID}`,
                element: <CampaignDetails />,
              },
              {
                path: `${ROUTING.EDIT_SCRAPER}/:${CAMPAIGN_ID}`,
                element: (
                  <LeadScraper
                    editable
                    title="common.helmetTitles.editScraper"
                    scraperSubtitleLocale="dashboard.navigation.editScraper"
                    submitButtonLocale="dashboard.editScraper.editCampaign"
                    breadCrumbsLocale="dashboard.navigation.campaignDetails"
                  />
                ),
              },
              {
                // Stripe redirects users here on successful Checkout. Mounted
                // inside PrivateRoutes so the user lands inside the
                // authenticated app shell. If their session lapsed during the
                // Stripe round-trip, PrivateRoutes will bounce them through
                // /login first.
                path: ROUTING.CHECKOUT_SUCCESS,
                element: (
                  <Suspense fallback={<Loading />}>
                    <CheckoutSuccess />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.CHECKOUT_CANCELLED,
                element: (
                  <Suspense fallback={<Loading />}>
                    <CheckoutCancelled />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.MAILBOX,
                element: (
                  <Suspense fallback={<Loading />}>
                    <UnifiedMailbox />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.ALL_CAMPAIGNS,
                element: (
                  <Suspense fallback={<Loading />}>
                    <AllCampaigns />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.EMAIL_CAMPAIGNS,
                element: <Navigate to={`/${ROUTING.ALL_CAMPAIGNS}`} replace />,
              },
              {
                path: ROUTING.EMAIL_ACCOUNTS,
                element: (
                  <Suspense fallback={<Loading />}>
                    <EmailAccounts />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.EMAIL_TEMPLATES,
                element: (
                  <Suspense fallback={<Loading />}>
                    <EmailTemplates />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.EMAIL_TEMPLATE_BUILDER,
                element: (
                  <Suspense fallback={<Loading />}>
                    <EmailTemplateBuilder />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.EMAIL_ANALYTICS,
                element: <Navigate to={`/${ROUTING.EMAIL_CAMPAIGNS}`} replace />,
              },
              {
                path: ROUTING.EMAIL_WARMUP,
                element: (
                  <Suspense fallback={<Loading />}>
                    <EmailWarmup />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.WARMUP_ONBOARDING,
                element: (
                  <Suspense fallback={<Loading />}>
                    <WarmupOnboarding />
                  </Suspense>
                ),
              },
              {
                path: 'create-campaign',
                element: <Navigate to={`/${ROUTING.QUICK_CAMPAIGN}`} replace />,
              },
              {
                path: 'new-scraper',
                element: <Navigate to={`/${ROUTING.QUICK_CAMPAIGN}`} replace />,
              },
              {
                path: ROUTING.QUICK_CAMPAIGN,
                element: (
                  <Suspense fallback={<Loading />}>
                    <QuickCampaign />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.IMPORT_CAMPAIGN,
                element: (
                  <Suspense fallback={<Loading />}>
                    <ImportCampaign />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.CUSTOM_DOMAINS,
                element: (
                  <Suspense fallback={<Loading />}>
                    <DomainDashboard />
                  </Suspense>
                ),
              },
              {
                path: `${ROUTING.CUSTOM_DOMAINS}/purchase`,
                element: (
                  <Suspense fallback={<Loading />}>
                    <DomainPurchaseWizard />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.EMAIL_INFRASTRUCTURE,
                element: (
                  <Suspense fallback={<Loading />}>
                    <EmailInfrastructureWizard />
                  </Suspense>
                ),
              },
              {
                path: `${ROUTING.CUSTOM_DOMAINS}/:id/provisioning`,
                element: (
                  <Suspense fallback={<Loading />}>
                    <ProvisioningProgress />
                  </Suspense>
                ),
              },
              {
                path: `${ROUTING.CUSTOM_DOMAINS}/:id`,
                element: (
                  <Suspense fallback={<Loading />}>
                    <DomainDetailsPage />
                  </Suspense>
                ),
              },
              {
                path: ROUTING.ADMIN_WARMUP_POOL,
                element: (
                  <AdminRoute>
                    <Suspense fallback={<Loading />}>
                      <AdminWarmupPool />
                    </Suspense>
                  </AdminRoute>
                ),
              },
              {
                path: ROUTING.ADMIN_ENGAGEMENT_DASHBOARD,
                element: (
                  <AdminRoute>
                    <Suspense fallback={<Loading />}>
                      <AdminEngagementDashboard />
                    </Suspense>
                  </AdminRoute>
                ),
              },
              {
                path: ROUTING.ADMIN_DNS_MANAGEMENT,
                element: (
                  <AdminRoute>
                    <Suspense fallback={<Loading />}>
                      <AdminDnsManagement />
                    </Suspense>
                  </AdminRoute>
                ),
              },
              {
                path: ROUTING.ONBOARDING,
                element: (
                  <Suspense fallback={<Loading />}>
                    <OnboardingDashboard />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  ...PUBLIC_ROUTES.map(({ path, Component }) => ({
    path,
    element: (
      <Suspense fallback={<Loading />}>
        <PublicRegistrationGuard>
          <RegistrationLayout>
            <Component key={path} />
          </RegistrationLayout>
        </PublicRegistrationGuard>
      </Suspense>
    ),
  })),
  {
    path: '*',
    element: (
      <Suspense>
        <NotFound />
      </Suspense>
    ),
  },
]);

const Routing = () => <RouterProvider router={router} />;

export default Routing;
